export const BOQ_STATUS = Object.freeze({
  DRAFT: 'draft',
  PUBLISHED_READY: 'published-ready',
  PUBLISHED_SENT: 'published-sent',
  DRAFT_NOT_ATTACHED: 'draft-not-attached',
  PUBLISHED_NOT_INCLUDED: 'published-not-included',
  UNKNOWN: 'unknown',
});

const STATUS_META = Object.freeze({
  [BOQ_STATUS.DRAFT]: {
    labelKey: 'boq-status-draft',
    noticeKey: 'boq-status-draft-notice',
    tone: 'draft',
    locked: false,
    canEdit: false,
    canMutate: true,
  },
  [BOQ_STATUS.PUBLISHED_READY]: {
    labelKey: 'boq-status-published-ready',
    noticeKey: 'boq-status-published-ready-notice',
    tone: 'published',
    locked: false,
    canEdit: true,
    canMutate: false,
  },
  [BOQ_STATUS.PUBLISHED_SENT]: {
    labelKey: 'boq-status-published-sent',
    noticeKey: 'boq-status-published-sent-notice',
    tone: 'sent',
    locked: true,
    canEdit: false,
    canMutate: false,
  },
  [BOQ_STATUS.DRAFT_NOT_ATTACHED]: {
    labelKey: 'boq-status-draft-not-attached',
    noticeKey: 'boq-status-draft-not-attached-notice',
    tone: 'locked',
    locked: true,
    canEdit: false,
    canMutate: false,
  },
  [BOQ_STATUS.PUBLISHED_NOT_INCLUDED]: {
    labelKey: 'boq-status-published-not-included',
    noticeKey: 'boq-status-published-not-included-notice',
    tone: 'locked',
    locked: true,
    canEdit: false,
    canMutate: false,
  },
  [BOQ_STATUS.UNKNOWN]: {
    labelKey: null,
    noticeKey: null,
    tone: 'locked',
    locked: true,
    canEdit: false,
    canMutate: false,
  },
});

const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const statusId = (status) =>
  status && typeof status === 'object' ? status.id : status;

const statusLabel = (status) => {
  if (status && typeof status === 'object') {
    return status.label ?? status.name;
  }
  if (typeof status === 'string') return status;
  return null;
};

const matchesProjectStatus = (entityStatus, projectStatus, fallbackLabel) => {
  if (!projectStatus) return false;

  const entityId = statusId(entityStatus);
  const projectId = statusId(projectStatus);
  if (
    entityId != null &&
    projectId != null &&
    String(entityId) === String(projectId)
  ) {
    return true;
  }

  const entityLabel = normalizeLabel(statusLabel(entityStatus));
  const projectLabel = normalizeLabel(
    statusLabel(projectStatus) || fallbackLabel
  );
  return Boolean(entityLabel && projectLabel && entityLabel === projectLabel);
};

/**
 * Resolves the presentation state without relying on environment-specific
 * project-status IDs. Unknown or not-yet-loaded statuses fail closed.
 */
export const resolveBoqStatus = (entity = {}, projectStatuses = {}) => {
  const hasEnquiry = Boolean(entity?.has_enquiry);
  const isDraft = matchesProjectStatus(
    entity?.status,
    projectStatuses?.draft,
    'draft'
  );
  const isPublished = matchesProjectStatus(
    entity?.status,
    projectStatuses?.published,
    'published'
  );
  const isTendered = matchesProjectStatus(
    entity?.status,
    projectStatuses?.tendered,
    'tendered'
  );

  let value = BOQ_STATUS.UNKNOWN;
  if (!hasEnquiry && isDraft) value = BOQ_STATUS.DRAFT;
  if (!hasEnquiry && isPublished) value = BOQ_STATUS.PUBLISHED_READY;
  if (hasEnquiry && isTendered) value = BOQ_STATUS.PUBLISHED_SENT;
  if (hasEnquiry && isDraft) value = BOQ_STATUS.DRAFT_NOT_ATTACHED;
  if (hasEnquiry && isPublished) value = BOQ_STATUS.PUBLISHED_NOT_INCLUDED;

  // Editing a ready published BoQ creates a local draft presentation state.
  if (value === BOQ_STATUS.PUBLISHED_READY && entity?.editing) {
    value = BOQ_STATUS.DRAFT;
  }

  return {
    value,
    ...STATUS_META[value],
  };
};
